export type ClientPortalNotificationType = 'violation' | 'info'

export interface ClientPortalNotification {
  id: string
  type: ClientPortalNotificationType
  title: string
  message: string
  createdAt: string
  readAt?: string
  meta?: Record<string, unknown>
}

const STORAGE_KEY = 'client_portal_notifications_v1'

function safeParse(raw: string | null): ClientPortalNotification[] {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(Boolean) as ClientPortalNotification[]
  } catch {
    return []
  }
}

function save(list: ClientPortalNotification[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, 200)))
}

export function listNotifications(): ClientPortalNotification[] {
  if (typeof window === 'undefined') return []
  return safeParse(localStorage.getItem(STORAGE_KEY))
}

export function addNotification(
  n: Omit<ClientPortalNotification, 'id' | 'createdAt'> & { id?: string; createdAt?: string },
) {
  if (typeof window === 'undefined') return
  const now = new Date().toISOString()
  const item: ClientPortalNotification = {
    id: n.id || `n_${Date.now()}_${Math.random().toString(16).slice(2)}`,
    createdAt: n.createdAt || now,
    type: n.type,
    title: n.title,
    message: n.message,
    readAt: n.readAt,
    meta: n.meta,
  }
  const existing = listNotifications()
  save([item, ...existing])
}

export function markAllRead() {
  if (typeof window === 'undefined') return
  const existing = listNotifications()
  const now = new Date().toISOString()
  save(existing.map((n) => (n.readAt ? n : { ...n, readAt: now })))
}

export function markRead(id: string) {
  if (typeof window === 'undefined') return
  const existing = listNotifications()
  const now = new Date().toISOString()
  save(existing.map((n) => (n.id === id ? { ...n, readAt: n.readAt || now } : n)))
}

export function unreadCount(): number {
  return listNotifications().filter((n) => !n.readAt).length
}

export function clearAllNotifications() {
  if (typeof window === 'undefined') return
  save([])
}

export function clearNotificationsByPredicate(predicate: (n: ClientPortalNotification) => boolean) {
  if (typeof window === 'undefined') return
  const existing = listNotifications()
  save(existing.filter((n) => !predicate(n)))
}

export function isVehicleViolationNotification(n: ClientPortalNotification): boolean {
  return n.type === 'violation'
}

export function clearVehicleViolationNotifications() {
  clearNotificationsByPredicate(isVehicleViolationNotification)
}

export function listPortalNotifications(options?: { includeVehicleViolations?: boolean }): ClientPortalNotification[] {
  const includeVehicleViolations = options?.includeVehicleViolations !== false
  const all = listNotifications()
  if (includeVehicleViolations) return all
  return all.filter((n) => !isVehicleViolationNotification(n))
}

export function portalUnreadCount(options?: { includeVehicleViolations?: boolean }): number {
  return listPortalNotifications(options).filter((n) => !n.readAt).length
}
